import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Event as EventIcon,
  CalendarMonth as CalendarMonthIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { API_URL } from '../../config/api';

interface BookingResp {
  booking_id: string;
  title: string;
  start_at: string;
  end_at: string;
  status: string;
  customer_name: string | null;
  customer_email: string | null;
  customer_phone: string | null;
  location_text: string | null;
  description: string | null;
  type_name: string | null;
}

const PublicBookingConfirmationPage: React.FC = () => {
  const { businessSlug, token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [businessName, setBusinessName] = useState('Check-Now');
  const [booking, setBooking] = useState<BookingResp | null>(null);
  const [cancelling, setCancelling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/public/booking/${businessSlug}/bookings/${token}`);
      setBusinessName(r.data.business?.name || 'Check-Now');
      setBooking(r.data.booking);
    } catch (e: any) {
      setError(e.response?.data?.message || 'Could not find your booking');
    } finally {
      setLoading(false);
    }
  }, [businessSlug, token]);

  useEffect(() => { load(); }, [load]);

  const downloadIcs = useCallback(() => {
    if (!booking) return;
    const fmt = (d: string) => dayjs(d).format('YYYYMMDDTHHmmss[Z]');
    const ics = [
      'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Check-Now//EN',
      'BEGIN:VEVENT',
      `UID:${booking.booking_id}@check-now.co.uk`,
      `DTSTAMP:${fmt(new Date().toISOString())}`,
      `DTSTART:${fmt(booking.start_at)}`,
      `DTEND:${fmt(booking.end_at)}`,
      `SUMMARY:${booking.title}`,
      booking.location_text ? `LOCATION:${booking.location_text}` : '',
      'END:VEVENT', 'END:VCALENDAR',
    ].filter(Boolean).join('\r\n');
    const blob = new Blob([ics], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'booking.ics';
    a.click();
    URL.revokeObjectURL(url);
  }, [booking]);

  const handleCancel = useCallback(async () => {
    if (!window.confirm('Cancel this booking?')) return;
    setCancelling(true);
    try {
      await axios.post(`${API_URL}/api/public/booking/${businessSlug}/bookings/${token}/cancel`, {});
      await load();
    } catch (e: any) {
      setError(e.response?.data?.message || 'Could not cancel');
    } finally {
      setCancelling(false);
    }
  }, [businessSlug, token, load]);

  if (loading) {
    return <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress /></Box>;
  }

  if (!booking) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Alert severity="error">{error || 'Booking not found'}</Alert>
      </Box>
    );
  }

  const isCancelled = booking.status === 'cancelled';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', py: { xs: 2, md: 6 } }}>
      <Container maxWidth="sm">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Avatar sx={{ width: 72, height: 72, mx: 'auto', mb: 2, bgcolor: isCancelled ? '#94a3b8' : '#22c55e' }}>
            {isCancelled ? <CancelIcon sx={{ fontSize: 40 }} /> : <CheckCircleIcon sx={{ fontSize: 40 }} />}
          </Avatar>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {isCancelled ? 'Booking cancelled' : 'You\'re booked in!'}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>
            {isCancelled ? 'This booking has been cancelled.' : `We've notified the ${businessName} team. They'll be in touch shortly.`}
          </Typography>
        </Box>

        <Card sx={{ borderRadius: 4, boxShadow: '0 10px 40px rgba(15,23,42,0.08)' }}>
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
              <EventIcon color="primary" />
              <Box>
                <Typography variant="h6">{booking.title}</Typography>
                {booking.type_name && (
                  <Typography variant="body2" color="text.secondary">{booking.type_name}</Typography>
                )}
              </Box>
            </Stack>
            <Divider sx={{ my: 2 }} />
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="caption" color="text.secondary">When</Typography>
                <Typography>{dayjs(booking.start_at).format('dddd, D MMMM YYYY')}</Typography>
                <Typography color="text.secondary">{dayjs(booking.start_at).format('HH:mm')} – {dayjs(booking.end_at).format('HH:mm')}</Typography>
              </Box>
              {booking.location_text && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Where</Typography>
                  <Typography>{booking.location_text}</Typography>
                </Box>
              )}
              {booking.description && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Notes</Typography>
                  <Typography>{booking.description}</Typography>
                </Box>
              )}
            </Stack>
          </CardContent>
          <Divider />
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
            {!isCancelled ? (
              <Button color="error" onClick={handleCancel} disabled={cancelling}>
                Cancel booking
              </Button>
            ) : <Box />}
            {!isCancelled && (
              <Button variant="contained" startIcon={<CalendarMonthIcon />} onClick={downloadIcs}>
                Add to calendar
              </Button>
            )}
          </Box>
        </Card>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 3 }}>
          Powered by Check-Now
        </Typography>
      </Container>
    </Box>
  );
};

export default PublicBookingConfirmationPage;
